/*
 * dashcam/ring.js — the bookkeeping of a loop recorder, with no I/O.
 *
 * A dash camera keeps the last N minutes and throws the rest away, and when
 * something happens it keeps a window around that moment for good. Both are
 * questions about WHICH encoded frames to keep, so the recorder never holds
 * video in memory: every encoded chunk goes to disk the moment it exists, as a
 * self-describing record, and this index remembers where each one lies.
 *
 * Record on disk (little-endian), HDR bytes of header then the payload:
 *   0  u8   MAGIC 0xDC
 *   1  u8   kind — 1 video keyframe, 2 video delta, 3 audio
 *   2  u16  reserved (0)
 *   4  f64  timestamp, microseconds since the session started
 *   12 u32  duration, microseconds
 *   16 u32  payload size, bytes
 *   20 u32  sequence number of the record in its session
 * Self-describing is the point: a tab that dies mid-drive leaves segment files
 * that can be read back without any index (see headersFromBytes / the storage
 * worker's `headers` op), so the minutes before a crash are not lost with it.
 *
 * Segments are storage buckets of ~SEG_US that always START with a video
 * keyframe (the recorder rotates only on a keyframe). That single rule makes
 * eviction safe — dropping the oldest segment never leaves the next one
 * beginning with frames that cannot be decoded — and it makes the pre-event
 * window cheap: the keyframe before the window's start is always in the
 * segment that overlaps it.
 *
 * Guards: tests/js/dashcam-ring.test.mjs.
 */

export const MAGIC = 0xDC;
export const HDR = 24;
export const KIND = Object.freeze({ KEY: 1, DELTA: 2, AUDIO: 3 });
/** One storage bucket. Short enough that a crash costs little, long enough that a 10-minute loop is ~60 files. */
export const SEG_US = 10e6;
/** Every second a keyframe: the pre-event window can start at most one second early. */
export const KEY_EVERY_US = 1e6;
/** One event never grows past this, however many impacts extend it. */
export const EVENT_MAX_US = 5 * 60e6;

export function isVideo(kind) { return kind === KIND.KEY || kind === KIND.DELTA; }

/**
 * Pack records into one buffer for a single append.
 * list: [{kind, ts, dur, data:Uint8Array, seq}] → {buf:Uint8Array, rel:[offset of each header]}
 */
export function packRecords(list) {
    let total = 0;
    for (const r of list) total += HDR + r.data.byteLength;
    const buf = new Uint8Array(total);
    const dv = new DataView(buf.buffer);
    const rel = [];
    let p = 0;
    for (const r of list) {
        rel.push(p);
        dv.setUint8(p, MAGIC);
        dv.setUint8(p + 1, r.kind);
        dv.setUint16(p + 2, 0, true);
        dv.setFloat64(p + 4, r.ts, true);
        dv.setUint32(p + 12, Math.max(0, Math.round(r.dur || 0)), true);
        dv.setUint32(p + 16, r.data.byteLength, true);
        dv.setUint32(p + 20, (r.seq >>> 0) || 0, true);
        buf.set(r.data, p + HDR);
        p += HDR + r.data.byteLength;
    }
    return { buf, rel };
}

/** Parse one header at `at` of a DataView; null when it is not a record. */
export function readHeader(dv, at) {
    if (at + HDR > dv.byteLength) return null;
    if (dv.getUint8(at) !== MAGIC) return null;
    const kind = dv.getUint8(at + 1);
    if (kind < 1 || kind > 3) return null;
    const ts = dv.getFloat64(at + 4, true);
    if (!isFinite(ts)) return null;
    return { kind, ts, dur: dv.getUint32(at + 12, true), size: dv.getUint32(at + 16, true), seq: dv.getUint32(at + 20, true) };
}

/**
 * Walk a whole segment held in memory. A torn tail (the tab died between the
 * header and the end of the payload) stops the walk: everything before it is
 * intact and returned. → {recs:[{kind,ts,dur,size,seq,off}], end, torn}
 */
export function headersFromBytes(u8, fileSize) {
    const size = fileSize == null ? u8.byteLength : fileSize;
    const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    const recs = [];
    let at = 0, torn = false;
    while (at < size) {
        const h = readHeader(dv, at);
        if (!h || at + HDR + h.size > size) { torn = true; break; }
        h.off = at;
        recs.push(h);
        at += HDR + h.size;
    }
    return { recs, end: at, torn };
}

/**
 * The worker's `headers` op hands back the raw header bytes and their file
 * offsets (reading only 24 bytes per record, not the video). → recs as above.
 */
export function headersFromWalk(hdrBytes, offs) {
    const dv = new DataView(hdrBytes.buffer, hdrBytes.byteOffset, hdrBytes.byteLength);
    const recs = [];
    for (let i = 0; i < offs.length; i++) {
        const h = readHeader(dv, i * HDR);
        if (!h) break;
        h.off = offs[i];
        recs.push(h);
    }
    return recs;
}

/**
 * The index of one recording session: segments in order, each with its
 * records. Nothing here touches bytes — `name` is what the store calls it.
 */
export class RingIndex {
    constructor() {
        this.segs = [];
        this.bytes = 0;
        this.lastVideoTs = -1;
        this.lastVideoEnd = -1;
        this.seq = 0;
    }

    get open() { const s = this.segs[this.segs.length - 1]; return s && s.open ? s : null; }

    /** Start a new bucket. The caller creates the file under the same name. */
    openSegment(name) {
        if (this.open) this.open.open = false;
        const seg = { name, t0: null, t1: null, bytes: 0, open: true, recs: [], video: 0 };
        this.segs.push(seg);
        return seg;
    }

    closeSegment() { if (this.open) this.open.open = false; }

    /** Should the next record start a new segment? Only ever before a keyframe. */
    wantsRotation(kind, ts, segUs = SEG_US) {
        const s = this.open;
        if (!s) return true;
        return kind === KIND.KEY && s.t0 != null && ts - s.t0 >= segUs;
    }

    /**
     * Account for a record appended to the open segment.
     * rec: {kind, ts, dur, size} → the stored record (with seg, off, seq).
     */
    add(rec) {
        const s = this.open;
        if (!s) throw new Error('ring: no open segment');
        if (s.recs.length === 0 && rec.kind === KIND.DELTA) throw new Error('ring: a segment must start with a keyframe');
        const r = { kind: rec.kind, ts: rec.ts, dur: rec.dur || 0, size: rec.size, off: s.bytes, seg: s, seq: this.seq++ };
        s.recs.push(r);
        s.bytes += HDR + rec.size;
        this.bytes += HDR + rec.size;
        if (isVideo(rec.kind)) {
            if (s.t0 == null) s.t0 = rec.ts;
            s.t1 = rec.ts + r.dur;
            s.video++;
            this.lastVideoTs = rec.ts;
            this.lastVideoEnd = rec.ts + r.dur;
        }
        return r;
    }

    /** Rebuild from records read back from disk (crash recovery). */
    static fromSegments(list) {
        const ix = new RingIndex();
        for (const { name, recs } of list) {
            const s = ix.openSegment(name);
            for (const h of recs) {
                if (s.recs.length === 0 && h.kind !== KIND.KEY) continue;   // cannot start on a delta
                const r = { kind: h.kind, ts: h.ts, dur: h.dur, size: h.size, off: h.off, seg: s, seq: ix.seq++ };
                s.recs.push(r);
                if (isVideo(h.kind)) {
                    if (s.t0 == null) s.t0 = h.ts;
                    s.t1 = h.ts + h.dur;
                    s.video++;
                    ix.lastVideoTs = h.ts;
                    ix.lastVideoEnd = h.ts + h.dur;
                }
            }
            s.bytes = s.recs.length ? s.recs[s.recs.length - 1].off + HDR + s.recs[s.recs.length - 1].size : 0;
            ix.bytes += s.bytes;
            s.open = false;
            if (!s.video) { ix.segs.pop(); ix.bytes -= s.bytes; }
        }
        return ix;
    }

    /** First and last video moment kept. */
    span() {
        const first = this.segs.find(s => s.t0 != null);
        if (!first) return null;
        return { t0: first.t0, t1: this.lastVideoEnd };
    }

    durationUs() { const s = this.span(); return s ? s.t1 - s.t0 : 0; }

    /**
     * Which segments the loop may forget now. `locks` are [start, end] ranges
     * (pending events) whose footage must survive; a locked segment also
     * shields everything after it, because a hole in the middle of the loop
     * would make the saved "last N minutes" jump.
     * Rule 1 — time: the oldest segment goes once the NEXT one alone reaches
     *   back to `lastVideoEnd - keepUs` (so what is kept always covers the full
     *   loop, never less).
     * Rule 2 — bytes: over `maxBytes`, the oldest goes regardless.
     * The open segment is never dropped.
     */
    evictable(keepUs, maxBytes = Infinity, locks = []) {
        const out = [];
        const segs = this.segs;
        let bytes = this.bytes;
        const now = this.lastVideoEnd;
        const locked = (s) => locks.some(([a, b]) => s.t0 != null && s.t0 <= b && (s.t1 == null || s.t1 >= a));
        for (let i = 0; i < segs.length - 1; i++) {
            const s = segs[i], next = segs[i + 1];
            if (s.open || locked(s)) break;
            const oldEnough = next.t0 != null && next.t0 <= now - keepUs;
            const tooBig = bytes > maxBytes;
            if (!oldEnough && !tooBig) break;
            out.push(s);
            bytes -= s.bytes;
        }
        return out;
    }

    /**
     * The disk refused a write in the middle of `seg` (storage full): only the
     * first `validBytes` of its file exist. Records past that point are
     * forgotten — an index that promises bytes the disk does not hold would
     * make every later save fail — and the segment is closed; the recorder
     * starts the next one on a fresh keyframe. → the number of records dropped.
     */
    truncateSegment(seg, validBytes) {
        const keep = seg.recs.filter(r => r.off + HDR + r.size <= validBytes);
        const dropped = seg.recs.length - keep.length;
        seg.recs = keep;
        const was = seg.bytes;
        seg.bytes = keep.length ? keep[keep.length - 1].off + HDR + keep[keep.length - 1].size : 0;
        this.bytes -= was - seg.bytes;
        seg.open = false;
        const vids = keep.filter(r => isVideo(r.kind));
        seg.video = vids.length;
        seg.t1 = vids.length ? vids[vids.length - 1].ts + vids[vids.length - 1].dur : null;
        if (!vids.length) seg.t0 = null;
        if (!seg.video) { this.segs = this.segs.filter(s => s !== seg); this.bytes -= seg.bytes; }
        // the newest picture the index still holds
        this.lastVideoTs = -1; this.lastVideoEnd = -1;
        for (let i = this.segs.length - 1; i >= 0 && this.lastVideoTs < 0; i--) {
            const v = this.segs[i].recs;
            for (let k = v.length - 1; k >= 0; k--) if (isVideo(v[k].kind)) { this.lastVideoTs = v[k].ts; this.lastVideoEnd = v[k].ts + v[k].dur; break; }
        }
        return dropped;
    }

    /** Forget segments (after the store removed their files). */
    drop(list) {
        const set = new Set(list);
        for (const s of list) this.bytes -= s.bytes;
        this.segs = this.segs.filter(s => !set.has(s));
    }

    /**
     * The records that make one playable file covering [a, b]:
     *   video from the last keyframe at or before `a` (or the first keyframe
     *   after it, when the loop does not reach that far back) to the last frame
     *   starting at or before `b`; audio from the first video frame to the end
     *   of the last one. → {video, audio, t0, t1} or null when nothing is there.
     */
    window(a, b) {
        const video = [], audio = [];
        let start = null;
        // the keyframe to start from: the last one at or before `a`, else the first after it
        outer: for (const s of this.segs) {
            for (const r of s.recs) {
                if (r.kind !== KIND.KEY) continue;
                if (r.ts <= a) { start = r; continue; }
                if (!start) start = r;
                break outer;
            }
        }
        if (!start || start.ts > b) return null;
        let on = false;
        for (const s of this.segs) {
            for (const r of s.recs) {
                if (!on) { if (r !== start) continue; on = true; }
                if (isVideo(r.kind)) { if (r.ts > b) break; video.push(r); }
            }
        }
        if (!video.length) return null;
        const t0 = video[0].ts;
        const last = video[video.length - 1];
        const t1 = last.ts + (last.dur || 0);
        for (const s of this.segs) {
            if (s.t1 != null && s.t1 < t0 - 1e6) continue;
            for (const r of s.recs) if (r.kind === KIND.AUDIO && r.ts >= t0 && r.ts < t1) audio.push(r);
        }
        return { video, audio, t0, t1 };
    }
}

/** Open an event window for a trigger at `ts`. `availT0` is where the loop begins. */
export function openEvent(ts, preUs, postUs, availT0) {
    const start = Math.max(availT0 == null ? ts - preUs : availT0, ts - preUs);
    return { start, end: ts + postUs, trig: [ts] };
}

/**
 * A second trigger while an event is still being recorded extends it instead
 * of starting another file — two files that overlap by a minute are worse than
 * one that simply runs longer. Returns false when the trigger comes after the
 * event already closed (the caller opens a new one).
 */
export function extendEvent(ev, ts, postUs, maxUs = EVENT_MAX_US) {
    if (ts > ev.end) return false;
    ev.end = Math.min(ev.start + maxUs, Math.max(ev.end, ts + postUs));
    ev.trig.push(ts);
    return true;
}

/** An event can be written once the recording has reached its end. */
export function eventReady(ev, lastVideoEnd) { return lastVideoEnd >= ev.end; }

/**
 * Bytes a loop of `loopUs` needs at `bitrate` bits/s (video + audio), with the
 * slack of one segment and of the keyframe overhead. Used to size the storage
 * check BEFORE recording starts.
 */
export function loopBytes(loopUs, bitrate, audioBitrate = 0) {
    const perSec = (bitrate + audioBitrate) / 8;
    return Math.ceil(perSec * (loopUs + SEG_US) / 1e6 * 1.15);
}

/** The longest loop (whole minutes from `choices`) that fits `freeBytes` next to `reserveBytes`. */
export function fitLoop(choicesMin, freeBytes, bitrate, audioBitrate, reserveBytes) {
    const sorted = choicesMin.slice().sort((x, y) => y - x);
    for (const m of sorted) {
        if (loopBytes(m * 60e6, bitrate, audioBitrate) + reserveBytes <= freeBytes) return m;
    }
    return 0;
}
